import { useTranslation } from 'react-i18next';

import { Button } from '@crm/shared/ui';

import { useCreateProjectMutation } from '../../api';

import type { TDeal, TDealDetailsAccessAction } from '@crm/entities/deal';

interface EmptyDashboardProps {
    dealId: TDeal['id'];
    onProjectCreated?: () => void;
    isAllowed: boolean;
    action: TDealDetailsAccessAction;
}

interface EmptyStateTextProps {
    className: string;
    title: string;
    description: string;
}

const EmptyStateText = ({ className, title, description }: EmptyStateTextProps) => (
    <div className={className}>
        <h1 className="m-0 self-stretch text-center text-[14px] leading-6 font-semibold text-gray-800">
            {title}
        </h1>
        <p className="self-stretch text-center text-[13px] leading-4 text-smtr-main-tertiary-100">
            {description}
        </p>
    </div>
);

export const EmptyDashboard = ({
    dealId,
    onProjectCreated,
    isAllowed,
    action,
}: EmptyDashboardProps) => {
    const { t } = useTranslation();
    const createProjectMutation = useCreateProjectMutation({ dealId });

    if (action !== 'create_project') {
        return (
            <EmptyStateText
                className="mx-auto flex h-full w-[800px] flex-col justify-center gap-1"
                title={t('crm.dealDetailsPage.emptyDashboard.noObjectAccess.title')}
                description={t('crm.dealDetailsPage.emptyDashboard.noObjectAccess.description')}
            />
        );
    }

    if (!isAllowed) {
        return (
            <div className="flex w-full flex-col items-center justify-center gap-3 p-10">
                <EmptyStateText
                    className="flex h-full w-full flex-col items-center justify-center gap-1"
                    title={t('crm.dealDetailsPage.emptyDashboard.noPermission.title')}
                    description={t('crm.dealDetailsPage.emptyDashboard.noPermission.description')}
                />
            </div>
        );
    }

    return (
        <div className="flex w-full flex-col items-center justify-center gap-3 p-10">
            <Button
                className="px-4 py-2"
                disabled={createProjectMutation.isPending}
                onClick={() =>
                    createProjectMutation.mutate(
                        { dealId },
                        {
                            onSuccess: onProjectCreated,
                        }
                    )
                }
                type="button"
            >
                {t('crm.dealDetailsPage.emptyDashboard.actions.addObjectInfo')}
            </Button>
            <span className="text-xs leading-4 font-normal text-smtr-main-tertiary-100">
                {t('crm.dealDetailsPage.emptyDashboard.description')}
            </span>
        </div>
    );
};
